import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/contexts/ThemeContext';
import { EnvironmentBanner } from './EnvironmentBanner';
import { adminAuthService } from '@/services/adminAuth';
import {
  Users,
  Plug,
  Settings,
  ArrowLeft,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  User,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  Shield,
} from 'lucide-react';
import { clsx } from 'clsx';

interface AdminLayoutProps {
  isBootstrapMode?: boolean;
}

export function AdminLayout({ isBootstrapMode = false }: AdminLayoutProps) {
  // Only use SSO auth when not in bootstrap mode
  // In bootstrap mode, we use adminAuthService instead
  const auth = useAuth();
  const { theme, setTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    if (isBootstrapMode) {
      adminAuthService.logout();
    } else {
      auth.logout();
    }
  };

  // Build nav items based on mode and SSO status
  // In bootstrap mode: only show Identity
  // In normal mode with active SSO: show all items (Users, Identity, Integrations, Configuration)
  const buildNavItems = () => {
    if (isBootstrapMode) {
      // Bootstrap mode: only Identity setup is available
      return [
        { path: '/admin-setup/dashboard/identity', icon: Shield, label: 'Identity' },
      ];
    }

    // Normal mode: show all items since user is authenticated via SSO
    return [
      { path: '/admin/users', icon: Users, label: 'Users' },
      { path: '/admin/identity', icon: Shield, label: 'Identity' },
      { path: '/admin/integrations', icon: Plug, label: 'Integrations' },
      { path: '/admin/configuration', icon: Settings, label: 'Configuration' },
    ];
  };

  const navItems = buildNavItems();
  const backPath = isBootstrapMode ? '/admin-setup' : '/';
  const backLabel = isBootstrapMode ? 'Back to Login' : 'Back to Portal';
  const displayName = isBootstrapMode ? 'System Administrator' : (auth.user?.display_name || auth.user?.email);

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ] as const;

  const sidebarWidth = sidebarCollapsed ? 'w-16' : 'w-64';
  const mainPadding = sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <EnvironmentBanner />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600/75 dark:bg-gray-900/75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 shadow-lg transform transition-all duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full',
          `lg:${sidebarWidth}`
        )}
        style={{ width: sidebarOpen ? '16rem' : undefined }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={clsx(
            'flex items-center h-16 border-b border-gray-200 dark:border-gray-700',
            sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'
          )}>
            {sidebarCollapsed ? (
              <div className="flex items-center justify-center">
                <Settings className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-2">
                  <Settings className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  <span className="font-semibold text-gray-900 dark:text-white">Admin Settings</span>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </>
            )}
          </div>

          {/* Back to portal/login link */}
          <div className={clsx(
            'border-b border-gray-200 dark:border-gray-700',
            sidebarCollapsed ? 'px-2 py-4' : 'px-2 py-4'
          )}>
            <NavLink
              to={backPath}
              className={clsx(
                'flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700',
                sidebarCollapsed ? 'justify-center p-2' : 'px-3 py-2'
              )}
              title={sidebarCollapsed ? backLabel : undefined}
            >
              <ArrowLeft className={clsx('h-5 w-5', !sidebarCollapsed && 'mr-3')} />
              {!sidebarCollapsed && backLabel}
            </NavLink>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-2">
            <div className="space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center text-sm font-medium rounded-md transition-colors',
                      sidebarCollapsed ? 'justify-center p-2' : 'px-3 py-2',
                      isActive
                        ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    )
                  }
                  onClick={() => setSidebarOpen(false)}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <item.icon className={clsx('h-5 w-5', !sidebarCollapsed && 'mr-3')} />
                  {!sidebarCollapsed && item.label}
                </NavLink>
              ))}
            </div>
          </nav>

          {/* Build version info */}
          {!sidebarCollapsed && (
            <div className="px-3 py-2">
              <div className="text-xs text-gray-400 dark:text-gray-600 font-mono">
                v{__APP_VERSION__} • {__BUILD_NUMBER__} • {__COMMIT_SHA__.substring(0, 7)}
              </div>
            </div>
          )}

          {/* Bottom section */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            {sidebarCollapsed ? (
              /* Collapsed state - vertical stack of icons */
              <div className="p-2 space-y-1">
                {/* Theme buttons */}
                {themeOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setTheme(option.value)}
                      className={clsx(
                        'w-full flex items-center justify-center p-2 rounded-md transition-colors',
                        theme === option.value
                          ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      )}
                      title={option.label}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  );
                })}

                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

                {/* User avatar */}
                <div
                  className="w-full flex items-center justify-center p-2"
                  title={displayName}
                >
                  <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  title="Sign out"
                >
                  <LogOut className="h-5 w-5" />
                </button>

                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

                {/* Expand button */}
                <button
                  onClick={() => setSidebarCollapsed(false)}
                  className="hidden lg:flex w-full items-center justify-center p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  title="Expand sidebar"
                >
                  <PanelLeft className="h-5 w-5" />
                </button>
              </div>
            ) : (
              /* Expanded state - clean horizontal layout */
              <div className="p-3 space-y-3">
                {/* User row with theme toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="flex-shrink-0 h-9 w-9 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {displayName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {isBootstrapMode ? 'Bootstrap Admin' : 'Admin'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    title="Sign out"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </div>

                {/* Theme and collapse row */}
                <div className="flex items-center justify-between">
                  {/* Theme toggle */}
                  <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    {themeOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.value}
                          onClick={() => setTheme(option.value)}
                          className={clsx(
                            'p-1.5 rounded-md transition-colors',
                            theme === option.value
                              ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                          )}
                          title={option.label}
                        >
                          <Icon className="h-4 w-4" />
                        </button>
                      );
                    })}
                  </div>

                  {/* Collapse button */}
                  <button
                    onClick={() => setSidebarCollapsed(true)}
                    className="hidden lg:flex items-center p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    title="Collapse sidebar"
                  >
                    <PanelLeftClose className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={clsx('transition-all duration-200', mainPadding)}>
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Menu className="h-6 w-6" />
            </button>
            <span className="font-semibold text-gray-900 dark:text-white">Admin Settings</span>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
